import { MbProFilterButton } from "@mb-pro-ui/components/table/components";
import { FilterViewProps } from "@mb-pro-ui/components/table/filters/base/types";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Icon,
  IconButton,
  lighten,
  Tooltip,
} from "@mui/material";
import isEmpty from "lodash/isEmpty";
import without from "lodash/without";
import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import {
  AutocompleteFilterButtonLabel,
  AutocompleteFilterProps,
  CustomPopper,
  ListboxComponent,
  PopperComponent,
  PopperContainer,
  renderAutocompleteInput,
  renderAutocompleteOption,
  VirtualizationContext,
} from "./AutocompleteFilter";
import Grow from "./Grow";
import { IdAware, isEqualById } from "./utils";

export interface MultiAutocompleteFilterProps<OptionType>
  extends AutocompleteFilterProps<OptionType> {
  showSelected?: boolean;
  confirmLabel?: string;
  simple?: boolean;
  shortLabel?: keyof OptionType | ((option: OptionType) => string);
}

const MultiAutocompleteFilter = <OptionType extends IdAware>({
  options,
  renderOption,
  getOptionLabel,
  getFilterValue,
  label,
  filterId,
  filterValue, // used only for reseting to empty
  urlSearchParam,
  getOptionDisabled,
  filterOptions,
  inputPlaceholder,
  showSelected,
  noOptionsText,
  virtualizedRowHeight = 50,
  sortBySelection = true,
  headerActions,
  handleClose,
  setFilterState,
  setDocTitle,
  confirmLabel,
  setFilterLabel,
  simple,
  shortLabel,
  popperContainerSx,
}: MultiAutocompleteFilterProps<OptionType> & FilterViewProps) => {
  // Popper anchor element
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // the actually selected values
  const [values, setValues] = useState<OptionType[]>([]);
  // the values selected in the <Autocomplete />
  const [pendingValues, setPendingValues] = useState<OptionType[]>([]);
  // the filter input value of the <Autocomplete />
  const [inputValue, setInputValue] = useState("");

  const getShortLabel = useCallback(
    (option: OptionType) => {
      if (shortLabel) {
        if (typeof shortLabel === "function") {
          return shortLabel(option);
        } else {
          return option[shortLabel];
        }
      } else {
        return getOptionLabel(option);
      }
    },
    [shortLabel, getOptionLabel],
  );

  const { formatMessage } = useIntl();

  useEffect(() => {
    setDocTitle?.(values.map(getOptionLabel).join(", "));
  }, [setDocTitle, values, getOptionLabel]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setPendingValues(values);
    setAnchorEl(event.currentTarget);
  };

  const handlePopupClose = () => {
    setValues(pendingValues);
    setInputValue("");
    handleClose?.();
    setAnchorEl(null);
  };

  const handleInputChange = (_: any, value: string, reason: string) => {
    if (reason === "input") {
      setInputValue(value);
    }
  };

  const handleSelectAllClick = () => {
    if (options) {
      if ((pendingValues && pendingValues.length) === options.length) {
        setPendingValues((v) => (v.length > 0 ? [] : v));
      } else {
        setPendingValues(options);
      }
    }
  };

  useEffect(() => {
    if (isEmpty(filterValue)) {
      setPendingValues((v) => (v.length > 0 ? [] : v));
      setValues((v) => (v.length > 0 ? [] : v));
    }
  }, [filterValue]);

  useEffect(() => {
    if (options && values) {
      if (options.length === values.length && !simple) {
        setPendingValues((v) => (v.length > 0 ? [] : v));
        setValues((v) => (v.length > 0 ? [] : v));
      }
    }
  }, [values, options, simple]);

  const consolidatedRef = useRef(false);

  useEffect(() => {
    if (options && consolidatedRef.current) {
      const filterValues = values.map(getFilterValue);

      if (filterValues.length > options.length / 2 && !simple) {
        const withoutFilterValues = without(
          options.map(getFilterValue),
          ...filterValues,
        );

        setFilterState(
          filterId,
          { value: withoutFilterValues, reversed: true },
          !isEmpty(withoutFilterValues)
            ? "-" + withoutFilterValues.join(",")
            : "",
        );
      } else {
        setFilterState(
          filterId,
          { value: filterValues },
          values.map((value) => getFilterValue(value)).join(","),
        );
      }
    }
  }, [values, filterId, getFilterValue, options, setFilterState, simple]);

  useEffect(() => {
    if (options && options.length > 0) {
      let paramsArray;

      if (urlSearchParam?.startsWith("-")) {
        paramsArray = without(
          options.map(getFilterValue),
          ...urlSearchParam.substring(1).split(","),
        );
      } else {
        paramsArray = urlSearchParam?.split(",");
      }

      paramsArray
        ? setValues(
            paramsArray
              .map((param) =>
                options.find((option) => param === getFilterValue(option)),
              )
              .filter(Boolean) as OptionType[],
          )
        : setValues([]);

      consolidatedRef.current = true;
    }
  }, [options, urlSearchParam, getFilterValue]);

  const open = Boolean(anchorEl);

  const sortedOptions = useMemo(() => {
    if (!options) {
      return [];
    }

    if (!sortBySelection || values.length === 0) {
      return options;
    }

    if (values.length === options.length) {
      return values;
    }

    const valueSet: Record<string, true> = {};
    let allSorted = true;

    for (let index = 0; index < values.length; index++) {
      const value = values[index];
      if (value.id !== options[index]?.id) {
        allSorted = false;
      }
      valueSet[value.id] = true;
    }

    if (allSorted) {
      return options;
    }

    return values.concat(options.filter((value) => !valueSet[value.id]));
  }, [sortBySelection, options, values]);

  const buttonLabel = label ?? formatMessage({ defaultMessage: "Select" });
  const buttonCounter = values && values.length > 0 ? `(${values.length})` : "";

  const valueLabel = values.map(getOptionLabel).join(", ");

  useEffect(() => {
    setFilterLabel?.(filterId, valueLabel);
  }, [filterId, valueLabel, setFilterLabel]);

  return (
    <>
      <MbProFilterButton
        variant="outlined"
        endIcon={
          <Icon fontSize="inherit">
            <DoneAllIcon />
          </Icon>
        }
        onClick={handleClick}
        active={open}
        emphasize={!isEmpty(filterValue)}
      >
        <AutocompleteFilterButtonLabel
          isEmpty={values.length === 0}
          valueLabel={valueLabel}
          buttonLabel={buttonLabel + " " + buttonCounter}
        />
      </MbProFilterButton>
      <CustomPopper open={open} anchorEl={anchorEl} placement="bottom-start">
        <ClickAwayListener onClickAway={handlePopupClose}>
          <PopperContainer sx={popperContainerSx}>
            <Box
              sx={{
                borderBottom: (theme) =>
                  `1px solid ${lighten(theme.palette.divider, 0.5)}`,
                padding: "8px 10px",
                display: "flex",
              }}
            >
              {headerActions}
              <Tooltip
                title={formatMessage({
                  defaultMessage: "Select All",
                })}
              >
                <IconButton onClick={handleSelectAllClick}>
                  <DoneAllIcon color="primary" />
                </IconButton>
              </Tooltip>
              <Grow />
              <Button onClick={handlePopupClose}>
                {confirmLabel ??
                  formatMessage({
                    defaultMessage: "Search",
                  })}
              </Button>
            </Box>
            {showSelected && pendingValues && pendingValues.length > 0 && (
              <Box
                sx={{
                  borderBottom: (theme) =>
                    `1px solid ${lighten(theme.palette.divider, 0.5)}`,
                  padding: "8px 10px",
                  maxHeight: (theme) => theme.spacing(12),
                  overflow: "auto",
                }}
              >
                {pendingValues.map((option) => (
                  <Chip
                    label={getShortLabel(option)}
                    key={option.id}
                    size="small"
                    variant="outlined"
                    color="primary"
                    avatar={<Avatar>U</Avatar>}
                    onDelete={() => {
                      setPendingValues((pValues) =>
                        pValues?.filter((value) => value.id !== option.id),
                      );
                    }}
                  />
                ))}
              </Box>
            )}
            <VirtualizationContext.Provider value={virtualizedRowHeight}>
              <Autocomplete
                value={pendingValues ?? []}
                options={sortedOptions}
                isOptionEqualToValue={isEqualById}
                getOptionDisabled={getOptionDisabled}
                open
                multiple
                onChange={(event, newValue, reason) => {
                  if (
                    event.type === "keydown" &&
                    (event as React.KeyboardEvent).key === "Backspace" &&
                    reason === "removeOption"
                  ) {
                    return;
                  }
                  setPendingValues(newValue);
                }}
                noOptionsText={noOptionsText}
                onClose={(_event, reason) => {
                  if (reason === "escape") {
                    handlePopupClose();
                  }
                }}
                renderInput={(params) =>
                  renderAutocompleteInput(
                    params,
                    inputPlaceholder,
                    setInputValue,
                  )
                }
                renderOption={(props, option, { selected }) =>
                  renderAutocompleteOption(
                    props,
                    option,
                    getOptionLabel,
                    renderOption,
                    selected,
                  )
                }
                inputValue={inputValue}
                onInputChange={handleInputChange}
                getOptionLabel={getOptionLabel}
                renderTags={() => null}
                disableCloseOnSelect
                PopperComponent={PopperComponent}
                ListboxComponent={ListboxComponent}
                filterOptions={filterOptions}
              />
            </VirtualizationContext.Provider>
          </PopperContainer>
        </ClickAwayListener>
      </CustomPopper>
    </>
  );
};

export default MultiAutocompleteFilter;
